<template>
  <v-dialog :value="value" width="70%" persistent>
    <v-card class="pa-0">
      <v-card-title>Period Range</v-card-title>
      <v-card-text class="black--text">
        <!-- Header Row -->
        <v-row class="align-center justify-center">
          <v-col cols="1"></v-col>
          <v-col><h3>From</h3></v-col>
          <v-col cols="auto" class="px-8"><h3>Nb of nights</h3></v-col>
          <v-col><h3>To</h3></v-col>
        </v-row>

        <!-- Current Period -->
        <v-row class="align-center">
          <v-col cols="1"><strong>Current :</strong></v-col>
          <v-col>
            <div class="d-flex align-center">
              <year-picker :value="data.period.startYear" disabled hideClear />
              <month-picker class="ml-2" :value="data.period.startMonth" disabled hideClear />
              <day-picker
                class="ml-2"
                :month="data.period.startMonth"
                :value="data.period.startDay"
                disabled
                hideClear
              />
            </div>
          </v-col>
          <v-col cols="auto" class="px-8">
            <f-number :value="data.nights" :disabled="true" />
          </v-col>
          <v-col>
            <div class="d-flex align-center">
              <year-picker :value="data.period.endYear" disabled hideClear />
              <month-picker class="ml-2" :value="data.period.endMonth" disabled hideClear />
              <day-picker class="ml-2" :month="data.period.endMonth" :value="data.period.endDay" disabled hideClear />
            </div>
          </v-col>
        </v-row>

        <!-- New Period -->
        <v-row class="align-center">
          <v-col cols="1"><strong>New :</strong></v-col>
          <v-col>
            <div class="d-flex flex-row align-center">
              <year-picker v-model="period.startYear" @input="updateStartDate" />
              <month-picker class="ml-2" v-model="period.startMonth" @input="updateStartDate" />
              <day-picker class="ml-2" :month="period.startMonth" v-model="period.startDay" @input="updateStartDate" />
            </div>
          </v-col>
          <v-col cols="auto" class="px-8">
            <f-number v-model="nights" @input="updateNumberOfNights" />
          </v-col>
          <v-col>
            <div class="d-flex align-center">
              <year-picker v-model="period.endYear" @input="updateEndDate" />
              <month-picker class="ml-2" v-model="period.endMonth" @input="updateEndDate" />
              <day-picker class="ml-2" :month="period.endMonth" v-model="period.endDay" @input="updateEndDate" />
            </div>
          </v-col>
        </v-row>

        <!-- Note -->
        <v-row class="mt-4">Note: Updating the range may exclude certain variable costs.</v-row>
      </v-card-text>

      <v-card-actions>
        <g-button label="Cancel" @onclick="onClickCancel" />
        <div class="flex-grow-1"></div>
        <g-button label="OK" @onclick="onOKCancel" :disabled="!isPeriodValid" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    value: Boolean,
    data: Object,
  },
  computed: {
    isPeriodValid() {
      const { startYear, startMonth, startDay, endYear, endMonth, endDay } = this.period;
      const nights = this.nights;
      return startYear && endYear && nights;
    },
  },
  data() {
    return {
      period: {
        startYear: null,
        startMonth: null,
        startDay: null,
        endYear: null,
        endMonth: null,
        endDay: null,
      },
      nights: null,
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (!newValue) return;
        this.period = { ...this.data.period };
        this.nights = this.data.nights;
      },
    },
  },
  methods: {
    createLocalDate(year, month, day) {
      return new Date(Number(year), Number(month) - 1, Number(day));
    },
    updateStartDate() {
      if (!this.data.id) this.period.endYear = this.period.startYear;
      if (!this.period.startMonth) this.period.startDay = null;
      this.autocompletePeriod({ isStartDateUpdated: true });
    },
    updateNumberOfNights() {
      this.autocompletePeriod({ isNumberOfNightsUpdated: true });
    },
    updateEndDate() {
      if (!this.period.endMonth) this.period.endDay = null;
      this.autocompletePeriod({ isEndDateUpdated: true });
    },
    autocompletePeriod({ isStartDateUpdated = false, isEndDateUpdated = false, isNumberOfNightsUpdated = false }) {
      const { startYear, startMonth, startDay, endYear, endMonth, endDay } = this.period;
      const hasStart = !!(startYear && startMonth && startDay);
      const hasEnd = !!(endYear && endMonth && endDay);
      const hasNights = this.nights !== null && this.nights !== undefined && this.nights !== "";

      // Changing start date or nights keeps the duration and shifts the end date.
      if ((isStartDateUpdated || isNumberOfNightsUpdated) && hasStart && hasNights) {
        const startDate = this.createLocalDate(startYear, startMonth, startDay);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + Number(this.nights));

        Object.assign(this.period, {
          endYear: endDate.getFullYear().toString(),
          endMonth: endDate.getMonth() + 1,
          endDay: endDate.getDate(),
        });
        return;
      }

      // Changing end date recalculates nights from the date range.
      if (isEndDateUpdated && hasStart && hasEnd) {
        const startDate = this.createLocalDate(startYear, startMonth, startDay);
        const endDate = this.createLocalDate(endYear, endMonth, endDay);
        const calculatedNights = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        this.nights = calculatedNights >= 0 ? calculatedNights : "";
        return;
      }

      if (!isNumberOfNightsUpdated && hasStart && hasEnd) {
        const startDate = this.createLocalDate(startYear, startMonth, startDay);
        const endDate = this.createLocalDate(endYear, endMonth, endDay);
        const calculatedNights = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        this.nights = calculatedNights >= 0 ? calculatedNights : "";
      } else if (!isEndDateUpdated && hasStart && hasNights) {
        const startDate = this.createLocalDate(startYear, startMonth, startDay);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + Number(this.nights));

        Object.assign(this.period, {
          endYear: endDate.getFullYear().toString(),
          endMonth: endDate.getMonth() + 1,
          endDay: endDate.getDate(),
        });
      } else if (!isStartDateUpdated && hasEnd && hasNights) {
        const endDate = this.createLocalDate(endYear, endMonth, endDay);
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - Number(this.nights));

        Object.assign(this.period, {
          startYear: startDate.getFullYear().toString(),
          startMonth: startDate.getMonth() + 1,
          startDay: startDate.getDate(),
        });
      }
    },
    onClickCancel() {
      this.$emit("input", false);
    },
    onOKCancel() {
      this.$emit("input", false);
      this.$emit("update", { period: { ...this.period }, nights: this.nights });
    },
  },
};
</script>
